import { INGREDIENTS_BY_ID, LUNCH_COMPONENTS_BY_ID, MEALS } from '../../data/index.js';
import type { Lookups } from '../../domain/requirements.js';
import type { Ingredient, Meal, Unit } from '../../domain/types.js';
import { AISLE_ORDER } from '../../domain/aisles.js';
import { dimensionOf } from '../../domain/units.js';
import { CUSTOM_PREFIX, type CustomMeal } from '../store/library.js';

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
/** Every Unit, so an unknown string is never looked up on a prototype. */
const UNIT_PROBE: Record<Unit, true> = { g: true, kg: true, ml: true, l: true, each: true };
/** An aisle every store's walking order knows; anything else throws in aisleRank. */
const isKnownAisle = (v: unknown): boolean =>
  typeof v === 'string' && Object.values(AISLE_ORDER).every((order) => (order as readonly string[]).includes(v));
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * A recipe from sync is whatever the other phone (or an older build) wrote.
 * The engine and the screens trust the Meal type, so anything that would
 * crash them is refused here. Storage is NOT touched: sync still round-trips
 * the bad copy, so a corrected one from the other phone can win.
 */
export function isUsableCustomMeal(value: unknown, ingredients: ReadonlyMap<string, Ingredient>): value is CustomMeal {
  if (!isRecord(value)) return false;
  const m = value;
  if (typeof m['id'] !== 'string' || !m['id'].startsWith(CUSTOM_PREFIX)) return false;
  if (typeof m['name'] !== 'string' || m['name'].trim() === '') return false;
  if (!isStringArray(m['tags']) || !isStringArray(m['method'])) return false;
  if (!isFiniteNumber(m['effortMinutes'])) return false;
  for (const key of ['cuisine', 'mainProtein', 'costBand', 'description']) {
    if (typeof m[key] !== 'string') return false;
  }
  const lines = m['ingredients'];
  if (!Array.isArray(lines)) return false;
  for (const line of lines) {
    if (!isRecord(line) || typeof line['ingredientId'] !== 'string' || typeof line['unit'] !== 'string') return false;
    const ingredient = ingredients.get(line['ingredientId']);
    if (!ingredient) return false;
    if (!isFiniteNumber(line['qtyPerPortion']) || line['qtyPerPortion'] < 0) return false;
    // 'cup' has no dimension at all; 'ml' of mince has the wrong one. Both throw in the engine.
    const dimension = Object.hasOwn(UNIT_PROBE, line['unit']) ? dimensionOf(line['unit'] as Unit) : undefined;
    if (dimension === undefined || dimension !== dimensionOf(ingredient.baseUnit)) return false;
  }
  const extras = m['extras'];
  if (extras !== undefined) {
    if (!Array.isArray(extras)) return false;
    for (const extra of extras) {
      if (!isRecord(extra) || typeof extra['label'] !== 'string') return false;
      const qty = extra['qtyPerPortion'];
      if (qty !== null && (!isFiniteNumber(qty) || qty < 0)) return false;
      if (typeof extra['unitLabel'] !== 'string' || !isKnownAisle(extra['aisle'])) return false;
    }
  }
  return true;
}

/**
 * Every meal the household can see, bundled and their own.
 *
 * The ONLY module in src/app allowed to import the bundled MEALS — there is a
 * test that says so. A screen reading MEALS directly would never see a custom
 * recipe, and the engine throws on a meal it cannot find.
 */
export interface MealCatalogue {
  /** What the picker offers. Archived recipes are hidden here. */
  pickable: Meal[];
  /** Everything resolvable, archived included: a planned week must always build. */
  byId: ReadonlyMap<string, Meal>;
  lookups: Lookups;
  customIds: ReadonlySet<string>;
}

/** Recipes that fail isUsableCustomMeal are left out of pickable AND byId, so a planned one reads as "still syncing". */
export function mealCatalogue(custom: readonly CustomMeal[]): MealCatalogue {
  const byId = new Map<string, Meal>(MEALS.map((m) => [m.id, m]));
  const customIds = new Set<string>();
  const usable = custom.filter((meal) => isUsableCustomMeal(meal, INGREDIENTS_BY_ID));
  for (const meal of usable) {
    // A bundled id is never overridden — that would silently change a meal
    // every planned week already relies on.
    if (byId.has(meal.id)) continue;
    byId.set(meal.id, meal);
    customIds.add(meal.id);
  }
  return {
    pickable: [...MEALS, ...usable.filter((m) => !m.archived && customIds.has(m.id))],
    byId,
    lookups: { ingredients: INGREDIENTS_BY_ID, meals: byId, lunchComponents: LUNCH_COMPONENTS_BY_ID },
    customIds,
  };
}
